import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import {
  Button,
  CoreLink,
  Main,
  ToggleGroup,
} from '../../components';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useRoutes from '../../hooks/useRoutes';
import WarningModal from './WarningModal';

import {
  auditQuestions,
  AUDIT_SCORE_THRESHOLD,
  computeAuditScore,
  isEligibleForResearch,
  screeningQuestions,
  type QuestionOption,
  type WarningMessageId,
} from '../../config/aidaScreeningQuestions';

import './style.scss';

const allQuestions = [
  ...screeningQuestions,
  ...auditQuestions,
];

const finalQuestionId = allQuestions[allQuestions.length - 1].id;

export type AidaAnswers = Record<number, number>;

const PageOnboardingAida: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { logout, joyride, onboarding } = useRoutes();
  const { buildClass } = useClasses('PageOnboardingAida', 'PageOnboardingAida--default');

  const [answers, setAnswers] = useState<AidaAnswers>({});
  const [activeWarning, setActiveWarning] = useState<WarningMessageId | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const questionsToShow = useMemo(() => {
    const answeredQuestionIds = Object.keys(answers).map(Number);

    const lastAnsweredQuestionId: number | null = answeredQuestionIds.length
      ? Math.max(...answeredQuestionIds)
      : null;

    const questionIdsToShow = lastAnsweredQuestionId === null
      ? [1]
      : [...answeredQuestionIds, Math.min(finalQuestionId, lastAnsweredQuestionId + 1)];

    return allQuestions.filter(
      (question) => questionIdsToShow.includes(question.id),
    );
  }, [answers]);

  const handleSubmit = useCallback(() => {
    if (Object.keys(answers).length < allQuestions.length) {
      setShowErrors(true);
      return;
    }

    const totalAuditValue = computeAuditScore(answers);

    if (totalAuditValue >= AUDIT_SCORE_THRESHOLD) {
      setActiveWarning('high_risk');
    } else if (isEligibleForResearch(answers)) {
      history.push({ pathname: onboarding.aidaConsent, state: { answers } });
    } else {
      history.push(joyride);
    }
  }, [answers, history, joyride, onboarding.aidaConsent]);

  return (
    <Page
      className={buildClass()}
      hideMenuMain
      hideChrome
      title={t('aida_screening.page_title')}
      headerContent={(
        <CoreLink
          to={logout}
          title={t('buttons.logout')}
          className={buildClass('header-logout')}
        />
      )}
    >
      <Container>
        <div className="row">
          <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 py-4 py-md-12">
            <Main className={buildClass('content')}>
              <h1 className={buildClass('title')}>{t('aida_screening.page_title')}</h1>
              <p className={`${buildClass('body')} body--large`}>
                {t('aida_screening.intro')}
              </p>

              <div className={buildClass('questions')}>
                {questionsToShow.map((question, index) => (
                  <ToggleGroup
                    key={question.id}
                    id={`aida-question-${question.id}`}
                    label={`${index + 1}/${allQuestions.length}. ${t(question.i18nKey)}`}
                    hint={question.type === 'multichoice' && question.hintKey
                      ? t(question.hintKey)
                      : undefined}
                    type={question.type}
                    options={(question.options as QuestionOption[]).map((o) => ({
                      label: t(o.labelKey),
                      value: String(o.value),
                    }))}
                    selectedValue={answers[question.id] !== undefined
                      ? String(answers[question.id])
                      : undefined}
                    onChange={(val) => {
                      const changedAnswer = allQuestions.find((q) => q.id === question.id);

                      if (!changedAnswer) {
                        return;
                      }

                      if (changedAnswer.type === 'binary') {
                        const warningId = changedAnswer.options.find(
                          (option) => option.value === Number(val),
                        )?.triggersWarning;

                        if (warningId) {
                          setActiveWarning(warningId);
                        }
                      }

                      setShowErrors(false);
                      setAnswers((prev) => ({
                        ...prev,
                        [question.id]: Number(val),
                      }));
                    }}
                    error={showErrors && answers[question.id] === undefined
                      ? t('aida_screening.validation')
                      : undefined}
                  />
                ))}
              </div>

              <div className={buildClass('submit')}>
                <Button onClick={handleSubmit}>
                  {t('aida_screening.submit')}
                </Button>
              </div>
            </Main>
          </div>
        </div>
      </Container>

      {activeWarning && (
        <WarningModal
          warningId={activeWarning}
          onClose={() => {
            if (activeWarning === 'high_risk') {
              setActiveWarning(null);
              history.push(joyride);
            } else {
              setActiveWarning(null);
            }
          }}
        />
      )}
    </Page>
  );
};

export default PageOnboardingAida;
